import { CODE_SYSTEMS, MEDICATION_CODE_SYSTEMS } from '@liza/fhir-utils';
import type { ActivityDefinition } from '@medplum/fhirtypes';

export const STATIN_ACTIVITY_URL = 'https://liza.health/fhir/ActivityDefinition/statin-moderate-intensity';
export const STATIN_ACTIVITY_VERSION = '1.0.0';
export const STATIN_ACTIVITY_CANONICAL = `${STATIN_ACTIVITY_URL}|${STATIN_ACTIVITY_VERSION}`;

/** RxNorm SCD for atorvastatin 20 MG Oral Tablet (RxNav, 2026-09-09). */
export const ATORVASTATIN_20_MG_TABLET_RXCUI = '617310';

/**
 * What the statin recommendation proposes when a clinician accepts it — the CPG-on-FHIR
 * "medication request activity" shape: the product and its dosage, applied into a MedicationRequest.
 *
 * The ADA recommends an *intensity*, not a drug. Atorvastatin 20 mg is the most common
 * moderate-intensity choice, so it is the default the suggestion carries; a prescriber who prefers
 * another statin rejects the suggestion and adds their own. Accepting puts the medication on the care
 * plan; prescribing still goes through the eRx flow like every other medication item.
 */
export const statinModerateIntensityActivity: ActivityDefinition = {
  resourceType: 'ActivityDefinition',
  url: STATIN_ACTIVITY_URL,
  version: STATIN_ACTIVITY_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: STATIN_ACTIVITY_URL }],
  name: 'statin-moderate-intensity',
  title: 'Moderate-intensity statin therapy',
  status: 'active',
  experimental: true,
  description:
    'Atorvastatin 20 mg daily as the default moderate-intensity statin for adults 40–75 with diabetes and no statin on their medication list. Any moderate-intensity statin satisfies the recommendation.',
  kind: 'MedicationRequest',
  intent: 'proposal',
  productCodeableConcept: {
    coding: [
      {
        system: MEDICATION_CODE_SYSTEMS.RXNORM,
        code: ATORVASTATIN_20_MG_TABLET_RXCUI,
        display: 'atorvastatin 20 MG Oral Tablet',
      },
    ],
    text: 'Atorvastatin 20 MG Oral Tablet',
  },
  dosage: [
    {
      text: '1 tablet by mouth daily',
      timing: { repeat: { frequency: 1, period: 1, periodUnit: 'd' } },
      route: { coding: [{ system: CODE_SYSTEMS.SNOMED, code: '26643006', display: 'Oral route' }], text: 'by mouth' },
      doseAndRate: [{ doseQuantity: { value: 1, unit: 'tablet' } }],
    },
  ],
  relatedArtifact: [
    {
      type: 'citation',
      display: 'ADA Standards of Care in Diabetes—2026, Recommendation 10.18',
      url: 'https://diabetesjournals.org/care/issue/49/Supplement_1',
    },
  ],
};
